import { dialog, InputJson, toDisplayCase } from "../../../../../nq-component";
import {
  getObjectUseCase,
  upsertUseCase,
} from "../../../../../usecases/object";
import withRouter from "../../../../../withRouter";
import BaseFormPage from "../../../../../base/BaseFormPage";
import NavBar from "../../../../../components/navbar";
import FormFactory from "../../../../../components/FormFactory";
import DialogModal from "../../../../../components/Dialog";
import DialogTable from "../../../../../components/DialogTable";
import BreakdownTable from "../../../../../components/BreakdownTable";
import ProductFormPresenter from "./ProductFormPresenter";

class ProductFormPage extends BaseFormPage {
  constructor(props) {
    super(props);
    this.state = {
      object: {},
      advanced: false,
      selectedIds: {
        raw_materials: [],
        others_raw_materials: [],
        components: [],
        otherComponents: [],
        additional_products: [],
      },
    };
    this.presenter = new ProductFormPresenter(
      this,
      getObjectUseCase(),
      upsertUseCase(),
    );
  }

  getCollectionName() {
    return "products";
  }

  onSubmitForm(e) {
    e.preventDefault();

    dialog.fire({
      html: (
        <DialogModal
          icon="bi bi-question-octagon-fill"
          iconColor="#82c14a"
          title="Confirm to Proceed"
          message="Are you sure want to continue this action?"
          isCancelButton={true}
          onSubmit={() => this.presenter.submit()}
          onClose={() => dialog.close()}
        />
      ),
      footer: false,
    });
  }

  handleOpenAdditionalProduct() {
    const schema = this.getSchema("products");

    dialog.fire({
      html: (
        <DialogTable
          title="Additional Products"
          where={{}}
          schema={schema}
          onCancel={() => dialog.close()}
          onSelect={(selectedItems) => {
            this.presenter._mergeSelectedProducts(selectedItems);
            dialog.close();
          }}
        />
      ),
      footer: false,
    });
  }

  handleChangeQuantityForProducts() {
    return (e, index) => {
      const newQuantity = parseFloat(e.target.value) || 0;
      this.presenter._updateProductQuantityAndRecalculate(index, newQuantity);
    };
  }

  async handleOpenComponent() {
    const schema = this.getSchema("components");
    const user = this.getCurrentUser();

    dialog.fire({
      html: (
        <DialogTable
          title="Components"
          where={{}}
          schema={schema}
          onCancel={() => dialog.close()}
          onSelect={(selectedItems) =>
            this.presenter._mergeSelectedComponents(selectedItems)
          }
        />
      ),
      footer: false,
    });
  }

  handleOpenRaw() {
    const schema = this.getSchema("raw_materials");

    dialog.fire({
      html: (
        <DialogTable
          title="raw_materials"
          where={{}}
          schema={schema}
          onCancel={() => dialog.close()}
          onSelect={(selectedItems) =>
            this.presenter._mergeSelectedRawMaterials(selectedItems)
          }
        />
      ),
      footer: false,
    });
  }

  handleOpenRawOthers() {
    const schema = this.getSchema("raw_materials");

    dialog.fire({
      html: (
        <DialogTable
          title="raw_materials"
          where={{}}
          schema={schema}
          onCancel={() => dialog.close()}
          onSelect={(selectedItems) =>
            this.presenter._mergeSelectedOthersRawMaterials(selectedItems)
          }
        />
      ),
      footer: false,
    });
  }

  onChange(value, field) {
    if (field === "itemName") {
      const hasValue = value !== undefined && value !== null && value !== "";
      this.setState({ hasQuantity: hasValue });
    }

    this.presenter.onChange(value, field);
  }

  handleDeleteSelectedFor(type) {
    const selected = this.state.selectedIds[type] || [];
    const updated = (this.state.object[type] || []).filter(
      (item) => !selected.includes(item.id),
    );

    this.setState((prev) => ({
      object: {
        ...prev.object,
        [type]: updated,
      },
      selectedIds: {
        ...prev.selectedIds,
        [type]: [],
      },
    }));
  }

  handleChangeQuantityFor(type) {
    return (e, index) => {
      const value = e.target.value;
      const updated = [...(this.state.object[type] || [])];
      updated[index] = { ...updated[index], quantity: value };

      if (type !== "components") {
        this.setState((prev) => ({
          object: { ...prev.object, [type]: updated },
        }));
        return;
      }

      // ---------- helpers ----------
      const toNum = (v) =>
        v === undefined || v === null || v === "" ? 0 : parseFloat(String(v));

      const addNum = (a, b, digits = 6) =>
        Number((toNum(a) + toNum(b)).toFixed(digits));

      const deriveUnitCost = (rm) => {
        const q = toNum(rm.quantity);
        const pc = toNum(rm.partial_cost);
        if (pc && q) return pc / q; // cost per UOM from component context
        const packCost = toNum(rm.cost);
        const packQty = toNum(rm.stock_quantity);
        return packCost && packQty ? packCost / packQty : 0;
      };

      // ---------- recompute component-derived raws ----------
      const compRawMap = new Map(); // totals from components only

      for (const comp of updated) {
        const baseQty = toNum(comp.component_quantity) || 1; // original size (e.g., 1 kg)
        const userQty = toNum(comp.quantity); // edited size (e.g., .2 kg)
        const scale = baseQty ? userQty / baseQty : 1;

        for (const raw of comp.raw_materials || []) {
          const prev = compRawMap.get(raw.id);
          const baseQ = toNum(raw.quantity); // raw usage per 1 component
          const qty = baseQ * scale; // scaled usage
          const unit = deriveUnitCost(raw);
          const cost = unit * qty;

          compRawMap.set(raw.id, {
            ...(prev || raw),
            quantity: addNum(prev?.quantity, qty),
            partial_cost: addNum(prev?.partial_cost, cost, 2),
            stock_quantity: prev?.stock_quantity ?? raw.stock_quantity ?? "",
          });
        }
      }

      // ---------- merge with existing raws (preserve manual raws) ----------
      this.setState((prev) => {
        const prevObj = prev.object || {};
        const prevRaws = prevObj.raw_materials || [];

        const merged = new Map();
        // 1) start with previous raws (manual ones will stay unless replaced)
        for (const r of prevRaws) merged.set(r.id, r);
        // 2) replace/insert component-derived raws with the recomputed totals
        for (const [id, r] of compRawMap) {
          const old = merged.get(id) || {};
          merged.set(id, { ...old, ...r }); // keep other fields, replace qty/cost
        }

        return {
          object: {
            ...prevObj,
            components: updated,
            raw_materials: Array.from(merged.values()),
          },
        };
      });
    };
  }

  handleSelectOneFor(type) {
    return (e, id) => {
      const checked = e.target.checked;
      this.setState((prev) => {
        const prevIds = prev.selectedIds[type] || [];
        const updated = checked
          ? [...prevIds, id]
          : prevIds.filter((itemId) => itemId !== id);

        return {
          selectedIds: {
            ...prev.selectedIds,
            [type]: updated,
          },
        };
      });
    };
  }

  handleSelectAllFor(type) {
    return (e) => {
      const checked = e.target.checked;
      const allIds = (this.state.object[type] || []).map((item) => item.id);
      this.setState((prev) => ({
        selectedIds: {
          ...prev.selectedIds,
          [type]: checked ? allIds : [],
        },
      }));
    };
  }

  getSelectedIds(type) {
    return this.state.selectedIds[type] || [];
  }

  renderBreakdownTable(title, type, onAdd, customChange) {
    const object = this.state.object;

    return (
      <BreakdownTable
        title={title}
        // recomputeFromComponents={() => this.recomputeFromComponents()}
        data={object[type] || []}
        selectedIds={this.getSelectedIds(type)}
        onSelectAll={this.handleSelectAllFor(type)}
        onSelectOne={this.handleSelectOneFor(type)}
        onChange={
          type === "products"
            ? this.handleChangeQuantityForProducts()
            : this.handleChangeQuantityFor(type)
        }
        onAdd={onAdd}
        onDelete={() => this.handleDeleteSelectedFor(type)}
        disableAdd={!this.state.hasQuantity && !this.getObjectId()}
        hasQuantity={this.state.hasQuantity || !!this.getObjectId()}
        showUOM={true}
        disableQty={type === "components" ? false : true}
      />
    );
  }

  render() {
    const object = this.state.object;
    const advanced = this.state.advanced;
    const schema = this.getSchema(this.getCollectionName());
    if (!schema) return <h1>no schema</h1>;
    const label = this.getObjectId() === undefined ? "Add " : "Edit ";
    return (
      <>
        <NavBar />
        <div className="overflow-auto">
          <div className="h-100">
            <div className="p-3 p-lg-4">
              <h2
                className="fw-bold mt-3 text-capitalize"
                style={{ color: "#82c14a" }}
              >
                <i
                  onClick={() => this.navigateBack()}
                  class="bi bi-chevron-compact-left"
                  style={{ color: "black" }}
                ></i>
                {label +
                  (schema.label || toDisplayCase(this.getCollectionName()))}
              </h2>
              <div
                className="mt-2 shadow rounded p-3 px-lg-5 py-lg-4"
                style={{ backgroundColor: "#f6f5fa" }}
              >
                <h6>Details</h6>
                <hr />
                <form onSubmit={this.onSubmitForm.bind(this)}>
                  <div className="row g-3">
                    {advanced ? (
                      <InputJson
                        defaultValue={object}
                        onChange={this.onChange.bind(this)}
                        rows="15"
                      />
                    ) : (
                      <FormFactory
                        schema={schema}
                        object={object}
                        onChange={this.onChange.bind(this)}
                        excludeFields={[
                          "raw_materials",
                          "others_raw_materials",
                          "additionalProduct",
                          "componentsBreakdown",
                          "rawMaterialsBreakdown",
                          "cost",
                          "products",
                          "components",
                        ]}
                      />
                    )}
                  </div>

                  {this.renderBreakdownTable(
                    "Additional Products",
                    "products",
                    () => this.handleOpenAdditionalProduct(),
                    this.handleChangeQuantityForProducts(),
                  )}

                  {this.renderBreakdownTable(
                    "Components Breakdown",
                    "components",
                    () => this.handleOpenComponent(),
                  )}

                  {/* {this.renderBreakdownTable(
                    "Raw Materials Breakdown",
                    "raw_materials",
                    () => this.handleOpenRaw(),
                  )} */}

                  {this.renderBreakdownTable(
                    "Other Raw Materials",
                    "others_raw_materials",
                    () => this.handleOpenRawOthers(),
                  )}

                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      className="btn fs-sm "
                      onClick={this.onClickBack.bind(this)}
                      style={{ backgroundColor: "#d1d1d1" }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn fs-sm text-white ms-3"
                      style={{ backgroundColor: "#82c14a" }}
                    >
                      <i className="bi text-white bi-file-earmark-check me-2"></i>
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default withRouter(ProductFormPage);
